import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Target, AlertTriangle, Award } from 'lucide-react';
import type { LearningPath, CheckpointScore } from '@shared/types/learning-path';
import { THICK_TILE_STYLE, THICK_TILE_CLASSNAME } from './tileStyles';

interface ReadinessBannerProps {
  examDate: string | null | undefined;
  examLabel: string | null | undefined;
  paths: LearningPath[];
}

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exam = new Date(dateStr + 'T00:00:00');
  const ms = exam.getTime() - today.getTime();
  return Math.round(ms / (24 * 60 * 60 * 1000));
}

/**
 * Simple readiness signal. Given each path's checkpoint scores and progress,
 * compute a weighted score:
 *   coverage = completedNodes / totalNodes
 *   mastery  = mean(checkpointScores) / 100
 *   readiness per path = 0.6 * mastery + 0.4 * coverage  (mastery weighted more)
 * The overall readiness is the average across paths, weighted by path size.
 *
 * This is intentionally coarse — domain weighting, exam-blueprint weightings,
 * and spaced-repetition decay come later.
 */
function computeReadiness(paths: LearningPath[]): { score: number; pathCount: number } {
  const completedPaths = paths.filter((p) => p.generationStatus === 'complete' && p.archived !== true);
  if (completedPaths.length === 0) return { score: 0, pathCount: 0 };

  let weightedSum = 0;
  let totalWeight = 0;

  for (const path of completedPaths) {
    const total = path.nodes.length;
    if (total === 0) continue;

    const completed = path.progress.completedNodes.length;
    const coverage = completed / total;

    const scores = Object.values(path.progress.checkpointScores ?? {}) as CheckpointScore[];
    const mastery = scores.length === 0
      ? coverage // if no checkpoint scores, mastery falls back to coverage
      : scores.reduce((sum, cp) => sum + cp.score, 0) / (scores.length * 100);

    const pathReadiness = 0.6 * mastery + 0.4 * coverage;
    const weight = total;

    weightedSum += pathReadiness * weight;
    totalWeight += weight;
  }

  const score = totalWeight > 0 ? Math.round((weightedSum / totalWeight) * 100) : 0;
  return { score, pathCount: completedPaths.length };
}

function readinessBand(score: number): { label: string; tone: string; message: string } {
  if (score >= 80) return { label: 'Ready', tone: '#55EFC4', message: 'Your mastery looks exam-ready across your paths. Keep drilling the weakest concepts until the day.' };
  if (score >= 60) return { label: 'On track', tone: '#F0A500', message: 'You\'re in good shape — a few weak spots remain. Lean into Test Me and Connect mode this week.' };
  if (score >= 35) return { label: 'Building', tone: '#A594F9', message: 'You\'re covering ground. Spend time on checkpoints to convert coverage into mastery.' };
  return { label: 'Early days', tone: '#FD79A8', message: 'Just getting started. Upload the material you\'ll be tested on and complete a checkpoint to activate your readiness signal.' };
}

export function ReadinessBanner({ examDate, examLabel, paths }: ReadinessBannerProps) {
  const days = examDate ? daysUntil(examDate) : null;
  const readiness = useMemo(() => computeReadiness(paths), [paths]);
  if (!examDate || days === null) return null;

  const band = readinessBand(readiness.score);
  const isPast = days < 0;
  const isUrgent = days >= 0 && days <= 7;

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${THICK_TILE_CLASSNAME} p-5`}
      style={THICK_TILE_STYLE}
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{ background: `radial-gradient(ellipse 60% 40% at 80% 50%, ${band.tone}22 0%, transparent 70%)` }}
      />
      <div className="relative flex items-center gap-5 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `${band.tone}22`, color: band.tone }}
          >
            {isPast ? <Award size={22} /> : isUrgent ? <AlertTriangle size={22} /> : <Target size={22} />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-heading text-sm font-bold text-content m-0">
                {isPast
                  ? `${examLabel ?? 'Exam'} — ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`
                  : `${examLabel ?? 'Your exam'} — ${days === 0 ? 'today' : `T-${days} day${days === 1 ? '' : 's'}`}`
                }
              </p>
              <span
                className="px-2 py-0.5 rounded-pill text-[10px] font-body font-bold tracking-wider uppercase"
                style={{ background: `${band.tone}22`, color: band.tone }}
              >
                {band.label}
              </span>
            </div>
            <p className="text-xs text-content-muted font-body mt-0.5 truncate">{band.message}</p>
          </div>
        </div>

        <div className="flex items-center gap-4 ml-auto shrink-0">
          <div className="text-right">
            <div className="text-[10px] text-content-dim font-body uppercase tracking-wider">Readiness</div>
            <div className="font-heading text-2xl font-bold" style={{ color: band.tone }}>
              {readiness.score}%
            </div>
          </div>
        </div>
      </div>

      {/* Readiness bar */}
      <div className="mt-4 h-1.5 rounded-pill bg-dark-subtle overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${readiness.score}%` }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="h-full rounded-pill"
          style={{ background: band.tone }}
        />
      </div>
      <p className="mt-2 text-[11px] text-content-dim font-body">
        Based on {readiness.pathCount} learning path{readiness.pathCount === 1 ? '' : 's'} — 60% mastery × 40% coverage. Domain-weighted readiness is on the roadmap.
      </p>
    </motion.section>
  );
}
